import React from 'react';
import { CafClusterListItem, CafLooksLike } from '../../../api/cafCorpus';
import { actionCopy } from '../copy';
import { formatAgo, formatCount } from '../format';
import { KebabMenu, MenuItem } from '../KebabMenu';
import { LenderOf } from './teachFilter';
import { WhoCell } from './WhoCell';

interface Props {
  rows: CafClusterListItem[];
  compact?: boolean;
  lenderOf: LenderOf;
  flashId?: string | null;
  /** Overrides the table's accessible name (a lender card needs its own, e.g. "Groups to label — <Lender>"). */
  ariaLabel?: string;
  onOpen: (id: string) => void;
  /** #705: open the group with the label of the approved example it looks like already filled in. */
  onLabelLike?: (id: string, like: CafLooksLike) => void;
  empty: React.ReactNode;
}

/** #705: "Looks like: Attach and send multi-rep email (“Multi-rep complaints…”)". Plain words only. */
export const looksLikeText = (like: CafLooksLike): string => {
  const what = actionCopy(like.action).label + (like.final_response_stance === 'final_response' ? ', final response' : '');
  const why = like.note.trim();
  return `Looks like: ${what}${why ? ` (“${why.length > 90 ? `${why.slice(0, 89)}…` : why}”)` : ''}`;
};

export const QueueTable: React.FC<Props> = ({ rows, compact = false, lenderOf, flashId, ariaLabel, onOpen, onLabelLike, empty }) => {
  if (!rows.length) return <div className="cur-empty">{empty}</div>;
  const menuFor = (group: CafClusterListItem, like: CafLooksLike, labelLike: NonNullable<Props['onLabelLike']>): MenuItem[] => [
    { key: 'like', label: 'Label it the same as that example', icon: 'copy', onSelect: () => labelLike(group.id, like) },
    { key: 'review', label: 'Review the emails first', icon: 'eye', onSelect: () => onOpen(group.id) },
  ];
  return (
    <div className="cur-table-wrap">
      <table className="cur-tbl" aria-label={ariaLabel ?? 'Groups to label'}>
        <colgroup>
          <col style={{ width: compact ? '30%' : '27%' }} /><col style={{ width: 84 }} /><col />
          <col style={{ width: 118 }} /><col style={{ width: 96 }} /><col style={{ width: 48 }} />
        </colgroup>
        <thead>
          <tr><th>{compact ? 'Sender' : 'Lender / sender'}</th><th>Emails</th><th>Sample subjects</th><th>First seen</th>
            <th><span className="cur-sr">Review</span></th><th><span className="cur-sr">Actions</span></th></tr>
        </thead>
        <tbody>
          {rows.map((group) => (
            <tr key={JSON.stringify([group.id, group.sender])} data-cur-group-id={group.id} tabIndex={0}
              className={`cur-row-click${flashId === group.id ? ' cur-just-added' : ''}`}
              onClick={() => onOpen(group.id)}
              onKeyDown={(event) => {
                // Enter/Space bubble up from a focusable descendant (e.g. the Review button); only
                // the row itself should open on a key press, never a click already handled below it.
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter') onOpen(group.id);
                else if (event.key === ' ') { event.preventDefault(); onOpen(group.id); }
              }}>
              <td><WhoCell sender={group.sender} lender={lenderOf(group.sender)} compact={compact} /></td>
              <td><span className="cur-count-pill">{formatCount(group.member_count)}</span></td>
              <td>
                {!group.sample_subjects || group.sample_subjects.length === 0
                  ? <span className="cur-muted">Open to read the emails</span>
                  : (
                    <ul className="cur-subjects">
                      {group.sample_subjects.map((subject, index) => (
                        <li key={`${index}-${subject}`} className="cur-ellip" title={subject}>{subject}</li>
                      ))}
                    </ul>
                  )}
                {group.looks_like && (
                  <div className="cur-looks-like cur-clamp2" title={looksLikeText(group.looks_like)}>{looksLikeText(group.looks_like)}</div>
                )}
              </td>
              <td className="cur-muted cur-nowrap">{formatAgo(group.created_at)}</td>
              <td className="cur-right">
                <button type="button" className="ui-btn ui-btn--primary ui-btn--sm"
                  onClick={(event) => { event.stopPropagation(); onOpen(group.id); }}>Review</button>
              </td>
              <td className="cur-right" onClick={(event) => event.stopPropagation()}>
                {group.looks_like && onLabelLike && (
                  <KebabMenu label={`Actions for the group from ${lenderOf(group.sender) ?? group.sender}`}
                    items={menuFor(group, group.looks_like, onLabelLike)} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
